import { reactive, ref, computed, getCurrentInstance, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

export function useFindPwdLogic() {
    const router = useRouter()
    const instance = getCurrentInstance()
    const { $request, $ValidConst } = instance.appContext.config.globalProperties

    // 表单数据
    const form = reactive({
        email: '',             // 邮箱
        code: '',              // 验证码
        newPwd: '',            // 新密码
        confirmPwd: ''         // 确认新密码
    })

    // 输入框引用
    const emailInput = ref(null)
    const codeInput = ref(null)
    const newPwdInput = ref(null)
    const confirmPwdInput = ref(null)

    // 错误状态
    const fieldError = reactive({
        email: false,
        code: false,
        newPwd: false,
        confirmPwd: false
    })

    // 错误提示文本
    const errorText = reactive({
        email: '',
        code: '',
        newPwd: '',
        confirmPwd: ''
    })

    // 验证码倒计时相关
    const isCountingDown = ref(false)
    const countDown = ref(60)
    let countDownTimer = null

    // 全局状态
    const globalError = ref('')
    const isLoading = ref(false)
    const shakeTrigger = ref(0)

    // 邮箱验证状态
    const isEmailValid = computed(() => {
        return form.email.trim() !== '' &&
            $ValidConst.EMAIL_REG.test(form.email.trim()) &&
            !fieldError.email;
    })

    // 验证码按钮文本
    const codeText = computed(() => {
        return isCountingDown.value ? `${countDown.value}秒后重新获取` : '获取验证码';
    })

    // 验证码按钮是否禁用
    const isCodeDisabled = computed(() => {
        return isCountingDown.value || !isEmailValid.value;
    })

    // 所有字段有效（控制提交状态）
    const isAllValid = computed(() => {
        return !fieldError.email &&
            !fieldError.code &&
            !fieldError.newPwd &&
            !fieldError.confirmPwd &&
            form.email.trim() &&
            form.code.trim() &&
            form.newPwd.trim() &&
            form.confirmPwd.trim()
    })

    // 输入处理（清空错误）
    const handleInput = (field) => {
        fieldError[field] = false
        errorText[field] = ''
        globalError.value = ''

        // 清空原生验证提示
        const inputEl = {
            email: emailInput.value,
            code: codeInput.value,
            newPwd: newPwdInput.value,
            confirmPwd: confirmPwdInput.value
        }[field]
        if (inputEl) {
            inputEl.setCustomValidity('')
        }
    }

    // 字段校验
    const checkField = (field) => {
        // 邮箱验证
        if (field === 'email') {
            if (form.email.trim() === '') {
                errorText.email = '请输入邮箱'
                fieldError.email = true
                return
            }
            if (!$ValidConst.EMAIL_REG.test(form.email.trim())) {
                errorText.email = $ValidConst.EMAIL_MSG
                fieldError.email = true
                return
            }
            fieldError.email = false
            errorText.email = ''
        }

        // 验证码验证
        if (field === 'code') {
            if (form.code.trim() === '') {
                errorText.code = '请输入验证码'
                fieldError.code = true
                return
            }
            if (!$ValidConst.CODE_REG.test(form.code.trim())) {
                errorText.code = $ValidConst.CODE_MSG
                fieldError.code = true
                return
            }
            fieldError.code = false
            errorText.code = ''
        }

        // 新密码验证
        if (field === 'newPwd') {
            if (form.newPwd.trim() === '') {
                errorText.newPwd = '请输入新密码'
                fieldError.newPwd = true
                return
            }
            if (!$ValidConst.PWD_REG.test(form.newPwd.trim())) {
                errorText.newPwd = $ValidConst.PWD_MSG
                fieldError.newPwd = true
                return
            }
            // 验证确认密码
            if (form.confirmPwd.trim() !== '') {
                checkField('confirmPwd')
            }
            fieldError.newPwd = false
            errorText.newPwd = ''
        }

        // 确认密码验证
        if (field === 'confirmPwd') {
            if (form.confirmPwd.trim() === '') {
                errorText.confirmPwd = '请确认新密码'
                fieldError.confirmPwd = true
                return
            }
            if (form.confirmPwd.trim() !== form.newPwd.trim()) {
                errorText.confirmPwd = '两次输入的密码不一致'
                fieldError.confirmPwd = true
                return
            }
            fieldError.confirmPwd = false
            errorText.confirmPwd = ''
        }
    }

    // 失去焦点校验
    const validateField = (field) => {
        if (!isLoading.value && form[field].trim() === '') {
            return
        }
        checkField(field)
    }

    // 设置全局错误并触发抖动
    const setGlobalError = (message) => {
        globalError.value = ''
        instance.proxy.$nextTick(() => {
            globalError.value = message
            shakeTrigger.value++
        })
    }

    // 获取验证码
    const getVerificationCode = async () =>  {
        checkField('email')
        if (!isEmailValid.value) return

        try {
            isCountingDown.value = true
            const formData = new FormData();
            formData.append('email', form.email);
            // 注意：实际项目中需使用正确的验证码接口
            const res = await $request.post('/login/sendCode', formData);

            if (res && res.code === 200) {
                setGlobalError(res.msg || '验证码已发送，请查收')
                let count = 60
                countDown.value = count

                if (countDownTimer) clearInterval(countDownTimer)

                countDownTimer = setInterval(() => {
                    count--
                    countDown.value = count
                    if (count <= 0) {
                        clearInterval(countDownTimer)
                        isCountingDown.value = false
                        countDownTimer = null
                    }
                }, 1000)
            } else {
                setGlobalError(res.msg || '发送验证码失败，请重试')
                isCountingDown.value = false
            }
        } catch (err) {
            setGlobalError(err.response?.data?.msg || err.message || '发送验证码失败，请重试')
            isCountingDown.value = false
        }
    }

    // 处理密码重置
    const handleResetPwd = async () => {
        // 清除之前的错误提示
        Object.keys(fieldError).forEach(key => {
            fieldError[key] = false
            errorText[key] = ''
        })
        globalError.value = ''

        // 全量校验
        checkField('email')
        checkField('code')
        checkField('newPwd')
        checkField('confirmPwd')

        // 检查是否有错误
        if (!isAllValid.value) {
            return
        }

        try {
            isLoading.value = true

            const res = await $request.post('/login/findPwd', {
                email: form.email,
                code: form.code,
                userPwd: form.newPwd
            })

            if (res && res.code === 200) {
                setGlobalError('密码重置成功，请登录')
                // 重置成功后跳转到登录页
                setTimeout(() => {
                    router.push('/login')
                }, 1500)
            } else {
                setGlobalError(res.msg || '密码重置失败，请稍后重试')
            }
        } catch (err) {
            setGlobalError(err.response?.data?.msg || err.message || '密码重置失败，请稍后重试')
        } finally {
            isLoading.value = false
        }
    }

    // 前往注册页面
    const goToRegister = () => {
        router.push('/register')
    }
    // 前往登录页面
    const goToLogin = () => {
        router.push('/login')
    }

    // 清理定时器
    const cleanup = () => {
        if (countDownTimer) {
            clearInterval(countDownTimer)
            countDownTimer = null
        }
    }

    // 组件卸载时清理（使用Vue的生命周期钩子替代window事件）
    onUnmounted(cleanup)

    // 自定义气泡提示显示逻辑
    const showTooltip = (e, text) => {
        if (!text) return
        e.target.setAttribute('title', text)
    }

    // 暴露所有需要在模板中使用的变量和方法
    return {
        form,
        emailInput,
        codeInput,
        newPwdInput,
        confirmPwdInput,
        fieldError,
        errorText,
        isCountingDown,
        countDown,
        globalError,
        isLoading,
        shakeTrigger,
        isEmailValid,
        codeText,
        isCodeDisabled,
        isAllValid,
        handleInput,
        validateField,
        setGlobalError,
        getVerificationCode,
        handleResetPwd,
        goToRegister,
        goToLogin,
        showTooltip
    }
}